import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useApiCacheRefresh } from '../lib/apiCache';
import { Layout } from './Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TrendingUp, DollarSign, Target, Users, Calendar, Download, 
  BarChart3, PieChart, ShieldAlert, Save, Briefcase, CheckCircle2,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, BarChart, Bar, ReferenceLine, ComposedChart
} from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { HelpTooltip } from './HelpTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS_PL = ['Styczeń','Luty','Marzec','Kwiecień','Maj','Czerwiec','Lipiec','Sierpień','Wrzesień','Październik','Listopad','Grudzień'];

const formatCurrency = (value) => {
  if (!value && value !== 0) return '0 zł';
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M zł`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(0)}k zł`;
  return `${value.toFixed(0)} zł`;
};

const ChangeIndicator = ({ value }) => {
  if (value === undefined || value === null || value === 0) return null;
  const isUp = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${isUp ? 'text-emerald-400' : 'text-red-400'}`}
      data-testid="change-indicator">
      {isUp ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {Math.abs(value).toFixed(0)}% vs poprz.
    </span>
  );
};


const KPIDashboardContent = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // View mode: 'month' or 'quarter'
  const [viewMode, setViewMode] = useState('month');
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Goal setting
  const [goalInput, setGoalInput] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isB2B = user?.role === 'handlowiec_b2b';

  // Month options
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${MONTHS_PL[d.getMonth()]} ${d.getFullYear()}`;
    monthOptions.push({ value, label });
  }

  const years = [];
  for (let y = 2024; y <= now.getFullYear() + 1; y++) years.push(y);

  const fetchData = useCallback(async (silent) => {
    try {
      if (silent !== true) setLoading(true);
      const params = {};
      if (viewMode === 'quarter') {
        params.quarter = selectedQuarter;
        params.year = selectedYear;
      } else {
        params.month = selectedMonth;
      }
      const response = await axios.get(`${API_URL}/reports/kpi`, { params });
      setData(response.data);
      if (response.data.monthly_goal) {
        setGoalInput(response.data.monthly_goal.toString());
      }
    } catch {
      toast.error('Błąd pobierania danych KPI');
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedMonth, selectedQuarter, selectedYear]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useApiCacheRefresh(() => fetchData(true));

  const handleSaveGoal = async () => {
    const amount = parseFloat(goalInput);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Podaj prawidłową kwotę celu');
      return;
    }
    setSavingGoal(true);
    try {
      let period;
      if (viewMode === 'quarter') {
        period = `q${selectedQuarter}_${selectedYear}`;
      } else {
        period = selectedMonth;
      }
      await axios.put(`${API_URL}/reports/kpi/goal`, { period, amount });
      toast.success('Cel zapisany');
      fetchData();
    } catch {
      toast.error('Błąd zapisu celu');
    } finally {
      setSavingGoal(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = viewMode === 'month' ? `?month=${selectedMonth}` : '';
      const response = await axios.get(`${API_URL}/reports/kpi/export${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kpi_${data?.current_month || 'report'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Raport pobrany');
    } catch {
      toast.error('Błąd eksportu');
    } finally {
      setExporting(false);
    }
  };

  // Chart data
  const prepareChartData = () => {
    if (!data?.daily_data) return [];
    let cumulative = 0;
    return data.daily_data.map(day => {
      cumulative += day.net_margin;
      return { ...day, date: day.date.substring(8), cumulative_margin: cumulative, goal_line: data.monthly_goal };
    });
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="text-[var(--crm-text-primary)]">Ładowanie danych KPI...</div></div>;
  }
  if (!data) return null;

  const chartData = prepareChartData();
  const goalReached = data.goal_progress_percent >= 100;
  const goal = data.monthly_goal || 1;
  const openPercent = Math.min((data.open_pipeline / goal) * 100, 100);
  const closedPercent = Math.min((data.closed_pipeline / goal) * 100, 100);

  const periodLabel = viewMode === 'quarter' 
    ? `Q${selectedQuarter} ${selectedYear}` 
    : monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl md:text-2xl font-manrope font-bold text-[var(--crm-text-primary)] flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-[#FF6B35]" />
              Dashboard KPI
            </h2>
            <p className="text-[var(--crm-text-secondary)] text-sm mt-1">{periodLabel}</p>
          </div>
          <Button onClick={handleExport} disabled={exporting} data-testid="kpi-export-button"
            className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap">
            <Download className="w-4 h-4 mr-2" />{exporting ? '...' : 'Excel'}
          </Button>
        </div>

        {/* View Mode Toggle + Selectors */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4" data-testid="kpi-toolbar">
          <div className="flex flex-wrap items-center gap-3">
            {/* Toggle */}
            <div className="flex bg-[var(--crm-bg-panel)] rounded-lg p-1 border border-[var(--crm-border)]" data-testid="kpi-view-toggle">
              <button onClick={() => setViewMode('month')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'month' ? 'bg-[#FF6B35] text-white' : 'text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]'}`}
                data-testid="kpi-toggle-month">
                <Calendar className="w-3.5 h-3.5 inline mr-1.5" />Miesiąc
              </button>
              <button onClick={() => setViewMode('quarter')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'quarter' ? 'bg-[#FF6B35] text-white' : 'text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]'}`}
                data-testid="kpi-toggle-quarter">
                <BarChart3 className="w-3.5 h-3.5 inline mr-1.5" />Kwartał
              </button>
            </div>

            {/* Period Selector */}
            {viewMode === 'month' ? (
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger data-testid="kpi-month-selector" className="w-48 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                  {monthOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-2">
                <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
                  <SelectTrigger data-testid="kpi-quarter-selector" className="w-24 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                    <SelectItem value="1">Q1</SelectItem>
                    <SelectItem value="2">Q2</SelectItem>
                    <SelectItem value="3">Q3</SelectItem>
                    <SelectItem value="4">Q4</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-24 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                    {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Goal Input (Admin only) */}
            {isAdmin && (
              <div className="flex items-center gap-2 ml-auto">
                <Target className="w-4 h-4 text-[#FF6B35] shrink-0" />
                <Input
                  type="number" value={goalInput} onChange={(e) => setGoalInput(e.target.value)}
                  placeholder="Cel (PLN)" data-testid="kpi-goal-input"
                  className="w-32 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-9 text-sm"
                />
                <Button onClick={handleSaveGoal} disabled={savingGoal} size="sm"
                  data-testid="kpi-goal-save"
                  className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white h-9 px-3">
                  <Save className="w-3.5 h-3.5 mr-1" />{savingGoal ? '...' : 'Zapisz'}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Pipeline Progress Bars: Open vs Closed vs Goal */}
      <Card className="bg-gradient-to-r from-[#243b5e] to-[#1a2d4d] border-[var(--crm-border)] p-6" data-testid="kpi-pipeline-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${goalReached ? 'bg-emerald-500/20' : 'bg-[#FF6B35]/20'}`}>
              <Target className={`w-6 h-6 ${goalReached ? 'text-emerald-500' : 'text-[#FF6B35]'}`} />
            </div>
            <div>
              <p className="text-[var(--crm-text-secondary)] text-sm">Realizacja celu {viewMode === 'quarter' ? 'kwartalnego' : 'miesięcznego'}</p>
              {isB2B ? (
                <p className="text-2xl md:text-3xl font-bold text-white" data-testid="kpi-goal-personal-label">
                  {data.has_personal_goal ? 'Twój cel osobisty' : 'Brak planu'}
                </p>
              ) : (
                <p className="text-2xl md:text-3xl font-bold text-white">
                  {formatCurrency(data.current_month_net_margin)}
                  <span className="text-[var(--crm-text-secondary)] text-lg font-normal"> / {formatCurrency(goal)}</span>
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p data-testid="kpi-goal-progress-percent" className={`text-4xl md:text-5xl font-bold ${isB2B && !data.has_personal_goal ? 'text-[var(--crm-text-muted)]' : goalReached ? 'text-emerald-400' : data.goal_progress_percent >= 70 ? 'text-amber-400' : 'text-red-400'}`}>
              {isB2B ? (data.has_personal_goal ? `${data.goal_progress_percent}%` : 'brak planu') : `${data.goal_progress_percent.toFixed(0)}%`}
            </p>
            <p className="text-[var(--crm-text-secondary)] text-sm">{data.days_passed} / {data.days_in_month} dni</p>
          </div>
        </div>

        {/* Open Pipeline Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm text-[var(--crm-text-secondary)] flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5 text-blue-400" />
              Zlecenia otwarte <span className="text-[var(--crm-text-muted)]">({data.open_projects})</span>
              <HelpTooltip text="Wartość projektów w toku (Lead, Akceptacja, Opłacone, Dostawa, W realizacji)" />
            </span>
            <span className="text-sm font-mono text-blue-400">{formatCurrency(data.open_pipeline)}</span>
          </div>
          <div className="relative h-6 bg-[var(--crm-bg-panel)] rounded-full overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-700" 
              style={{ width: `${openPercent}%` }} />
            {/* Goal marker at 100% */}
            <div className="absolute inset-y-0 bg-white/80 w-0.5" style={{ left: `${Math.min(100, (goal / Math.max(data.open_pipeline, data.closed_pipeline, goal) * 100))}%` }} />
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white drop-shadow">
              {!isB2B && `${openPercent.toFixed(0)}% celu`}
            </span>
          </div>
        </div>

        {/* Closed Pipeline Bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm text-[var(--crm-text-secondary)] flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              Zlecenia zamknięte <span className="text-[var(--crm-text-muted)]">({data.closed_projects})</span>
              <HelpTooltip text="Wartość projektów zakończonych w wybranym okresie" />
            </span>
            <span className="text-sm font-mono text-emerald-400">{formatCurrency(data.closed_pipeline)}</span>
          </div>
          <div className="relative h-6 bg-[var(--crm-bg-panel)] rounded-full overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full transition-all duration-700"
              style={{ width: `${closedPercent}%` }} />
            <div className="absolute inset-y-0 bg-white/80 w-0.5" style={{ left: `${Math.min(100, (goal / Math.max(data.open_pipeline, data.closed_pipeline, goal) * 100))}%` }} />
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white drop-shadow">
              {!isB2B && `${closedPercent.toFixed(0)}% celu`}
            </span>
          </div>
        </div>

        {data.goal_progress_percent >= 100 && (
          <p className="text-emerald-400 text-sm mt-3 text-center font-medium">
            Cel przekroczony o {formatCurrency(data.current_month_net_margin - goal)}!
          </p>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            <span className="text-[var(--crm-text-secondary)] text-xs">Wartość ofert</span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-[var(--crm-text-primary)]">{formatCurrency(data.total_offer)}</p>
        </Card>
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-amber-500" />
            <span className="text-[var(--crm-text-secondary)] text-xs">Marża netto</span>
          </div>
          <p className={`text-xl md:text-2xl font-bold ${data.total_net_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {formatCurrency(data.total_net_margin)}
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-amber-400 text-xs">{data.avg_net_margin_percent.toFixed(1)}%</span>
            {data.previous_period && <ChangeIndicator value={data.previous_period.margin_change} />}
          </div>
        </Card>
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <Briefcase className="w-5 h-5 text-blue-400" />
            <span className="text-[var(--crm-text-secondary)] text-xs">Nowe projekty</span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-[var(--crm-text-primary)]">{data.open_projects + data.closed_projects}</p>
          {data.previous_period && <ChangeIndicator value={data.previous_period.projects_change} />}
        </Card>
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <span className="text-[var(--crm-text-secondary)] text-xs">Zakończone</span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-[var(--crm-text-primary)]">{data.closed_projects}</p>
          {data.previous_period && <ChangeIndicator value={data.previous_period.completed_change} />}
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cumulative Margin Chart */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 md:p-6">
          <h3 className="text-[var(--crm-text-primary)] font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#FF6B35]" />
            Dynamika marży netto
          </h3>
          <div className="h-64 md:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334E78" />
                <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1a2d4d', border: '1px solid #334E78', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value, name) => [
                    `${value?.toLocaleString('pl-PL')} zł`,
                    name === 'cumulative_margin' ? 'Marża' : 'Cel'
                  ]}
                />
                <Legend />
                <Line type="monotone" dataKey="cumulative_margin" stroke="#FF6B35" strokeWidth={3} name="Marża netto" dot={false} />
                <Line type="monotone" dataKey="goal_line" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" name="Cel" dot={false} />
                <ReferenceLine y={goal} stroke="#10b981" strokeDasharray="8 4" label={{ value: '100% celu', fill: '#10b981', fontSize: 11, position: 'right' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* User Breakdown */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 md:p-6">
          <h3 className="text-[var(--crm-text-primary)] font-semibold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#FF6B35]" />
            Rozbicie na użytkowników ({periodLabel})
          </h3>
          {data.user_data.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-[var(--crm-text-secondary)]">Brak danych</div>
          ) : (
            <div className="h-64 md:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.user_data} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334E78" />
                  <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="initials" stroke="#94a3b8" tick={{ fontSize: 12 }} width={50} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a2d4d', border: '1px solid #334E78', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value, name) => [`${value?.toLocaleString('pl-PL')} zł`, name === 'net_margin' ? 'Marża netto' : 'Oferty']}
                  />
                  <Legend />
                  <Bar dataKey="total_offer" fill="#3b82f6" name="Oferty" />
                  <Bar dataKey="net_margin" fill="#FF6B35" name="Marża netto" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* 12-Month History Bar Chart */}
      {data.monthly_history && data.monthly_history.length > 0 && (
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 md:p-6" data-testid="kpi-monthly-history-chart">
          <h3 className="text-[var(--crm-text-primary)] font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#FF6B35]" />
            Ostatnie 12 miesięcy — zakończone projekty i marża
          </h3>
          <div className="h-64 md:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.monthly_history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334E78" />
                <XAxis dataKey="label" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" stroke="#94a3b8" tick={{ fontSize: 10 }}
                  tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a2d4d', border: '1px solid #334E78', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value, name) => {
                    if (name === 'Marża netto') return [`${value?.toLocaleString('pl-PL')} zł`, name];
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar yAxisId="right" dataKey="completed" fill="#3b82f6" name="Zakończone" radius={[4, 4, 0, 0]} />
                <Line yAxisId="left" type="monotone" dataKey="margin" stroke="#FF6B35" strokeWidth={2.5}
                  name="Marża netto" dot={{ fill: '#FF6B35', r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* User Table */}
      {data.user_data.length > 0 && (
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4 md:p-6 overflow-x-auto">
          <h3 className="text-[var(--crm-text-primary)] font-semibold mb-4">Szczegóły per użytkownik</h3>
          <Table className="min-w-[500px]">
            <TableHeader>
              <TableRow className="border-b border-[var(--crm-border)]">
                <TableHead className="text-left text-[var(--crm-text-secondary)] text-sm py-2">Użytkownik</TableHead>
                <TableHead className="text-right text-[var(--crm-text-secondary)] text-sm py-2">Zleceń</TableHead>
                <TableHead className="text-right text-[var(--crm-text-secondary)] text-sm py-2">Wartość ofert</TableHead>
                <TableHead className="text-right text-[var(--crm-text-secondary)] text-sm py-2">Marża netto</TableHead>
                <TableHead className="text-right text-[var(--crm-text-secondary)] text-sm py-2">% celu</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.user_data.map((u) => (
                <TableRow key={u.user_id || u.email} className="border-b border-[var(--crm-border)]/50 hover:bg-[var(--crm-bg-card-hover)]">
                  <TableCell className="py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#FF6B35]/20 flex items-center justify-center">
                        <span className="text-[#FF6B35] text-xs font-bold">{u.initials}</span>
                      </div>
                      <span className="text-[var(--crm-text-primary)] text-sm hidden sm:inline">{u.email}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-[var(--crm-text-primary)] py-3">{u.projects_count}</TableCell>
                  <TableCell className="text-right text-[var(--crm-text-secondary)] py-3 font-mono">{u.total_offer?.toLocaleString('pl-PL')} zł</TableCell>
                  <TableCell className={`text-right py-3 font-mono font-bold ${u.net_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {u.net_margin?.toLocaleString('pl-PL')} zł
                  </TableCell>
                  <TableCell className="text-right text-amber-400 py-3">
                    {goal > 0 ? ((u.net_margin / goal) * 100).toFixed(0) : 0}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export const KPIDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user?.role === 'montaz' || user?.role === 'guest' || user?.role === 'duch') {
    return (
      <Layout>
        <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-8 text-center max-w-md">
            <ShieldAlert className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[var(--crm-text-primary)] mb-2">Brak dostępu</h2>
            <p className="text-[var(--crm-text-secondary)] mb-4">
              Dashboard KPI jest dostępny tylko dla administratorów i asystentów biura.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white">
              Powrót do strony głównej
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8">
        <KPIDashboardContent />
      </div>
    </Layout>
  );
};
